<script setup lang="ts">
import { computed, ref } from 'vue';
import type { GameItem } from '~/utils/perfilOficialConfigs';
import type { SpacingProp } from '@/types/Layout';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';

type DisplayMode = 'list' | 'slide';

type Button = {
  url: string;
  text: string;
};

type Props = {
  button?: Button;
  items?: GameItem[];
  title?: string;
  iconPath?: string;
  seeMoreUrl?: string;
  type?: MediaType;
  isLoading?: boolean;
  isLoadingMoreGames?: boolean;
  slug?: string;
  displayMode?: DisplayMode;
  /**
   * Items per row.
   * In order as number or number list
   *
   * itemsPerRow or [desktop, tablet, mobile]
   *
   * Ex: 8 or [8, 6, 4]
   */
  perRow?: SpacingProp;
  /**
   * Space between items.
   * In order as number or number list
   *
   * spaceBetweenItems or [desktop, tablet, mobile]
   *
   * Ex: 24 or [24, 16, 12]
   */
  spaceBetween?: SpacingProp;
  isFirstChildBigger?: boolean;
  shouldNormalizeThumbnail?: boolean;
  cardHeight?: string;
  showNumbers?: boolean;
  /**
   * Loading strategy for number images (1-10).
   * Use 'eager' for above-the-fold components.
   */
  loadingNumbers?: 'lazy' | 'eager';
  /**
   * Disables lazy loading for game images (above-the-fold components).
   * When true, all game images will load eagerly instead of lazy.
   */
  disableLazyLoading?: boolean;
  /**
   * Number of items to load eagerly on mobile (rest will be lazy).
   * Only applies when disableLazyLoading is true.
   * If not set, all items will load eagerly.
   */
  mobileEagerCount?: number;
};

const {
  button,
  items,
  title,
  iconPath,
  seeMoreUrl,
  type = 'game',
  isLoading = false,
  isLoadingMoreGames,
  displayMode = 'slide',
  perRow = [6, 4, 3.4],
  spaceBetween = [24, 16, 10],
  isFirstChildBigger,
  cardHeight = 'auto',
  showNumbers = false,
  loadingNumbers = 'lazy',
  disableLazyLoading = false,
  mobileEagerCount,
} = defineProps<Props>();

const { getSpacingMeasurementFromPropList, isMobile } = useLayout();
const { getLoadingStrategy } = useImageLoading();

const MAX_NUMERIC_ITEMS = 10;
const MAX_NUMERIC_IMAGES = 10; // número de arquivos 00X-number.webp disponíveis

// Quando showNumbers está ativo, limita a 10 itens (máximo de números disponíveis)
const displayItems = computed(() => {
  if (!items) return [];
  if (showNumbers) return items.slice(0, MAX_NUMERIC_ITEMS);

  return items;
});

// Refs por índice dos cards do top10. O botão JOGAR vive fora do <a> do card
// (a metade de baixo "vaza" pra fora por design), então delegamos o clique
// dele pro mesmo navigate() do card — mantendo a lógica mobile/desktop num só lugar.
type CardWithNavigate = { navigate: () => void };

const cardRefs = ref<Record<number, CardWithNavigate | null>>({});

function setCardRef(el: unknown, index: number) {
  if (el) cardRefs.value[index] = el as CardWithNavigate;
  else delete cardRefs.value[index];
}

function onTop10PlayClick(index: number) {
  cardRefs.value[index]?.navigate();
}

const isSlideMode = computed<boolean>(() => {
  return displayMode === 'slide';
});

const isGameMediaType = computed<boolean>(() => {
  return type === 'game';
});

const spaceBetweenItems = computed<number>(() => {
  return getSpacingMeasurementFromPropList(spaceBetween, 16);
});

const spaceBetweenInPixels = computed<string>(() => {
  return spaceBetweenItems.value + 'px';
});

const itemsPerRow = computed<number>(() => {
  return getSpacingMeasurementFromPropList(perRow, 6);
});

// Se disableLazyLoading = true, todos os itens carregam eager
// Se mobileEagerCount está definido e é mobile, apenas os primeiros N itens são eager
// Caso contrário, usa lazy loading (0 = tudo lazy)
const itemsAboveFold = computed<number>(() => {
  if (disableLazyLoading) {
    // Em mobile, se mobileEagerCount definido, carrega apenas os primeiros N eager
    if (isMobile.value && mobileEagerCount !== undefined) {
      return mobileEagerCount;
    }

    return displayItems.value.length; // TODOS EAGER
  }

  return 0; // TUDO LAZY
});

// Retorna 'eager' ou 'lazy' para a imagem do número baseado no índice
const getNumberLoadingStrategy = (index: number): 'eager' | 'lazy' => {
  if (loadingNumbers === 'lazy') return 'lazy';
  // Em mobile, se mobileEagerCount definido, apenas os primeiros N são eager
  if (isMobile.value && mobileEagerCount !== undefined) {
    return index < mobileEagerCount ? 'eager' : 'lazy';
  }

  return 'eager';
};

const mediaItemWidth = computed<string>(() => {
  const gapValue = spaceBetweenItems.value;
  const items = itemsPerRow.value;

  return `calc((100% - ${(items - 1) * gapValue}px) / ${items})`;
});

// Calcula altura do card usando 'auto' para permitir que o aspect-ratio do CSS funcione
// O aspect-ratio garante que o espaço seja reservado antes das imagens carregarem
const computedCardHeight = computed<string>(() => {
  // Se cardHeight foi explicitamente definido (não é 'auto'), usa ele
  if (cardHeight !== 'auto') return cardHeight;

  // Usa 'auto' para permitir que o CSS com aspect-ratio controle a altura
  // Isso evita conflitos e garante comportamento responsivo
  return 'auto';
});
</script>

<template>
  <div
    class="mb-5 md:mb-7"
    :class="$style.listGames"
  >
    <template v-if="isLoading">
      <div :class="$style.title">
        <BaseCommonSkeleton
          v-if="title"
          :class="$style.skeletonTitle"
        />
        <BaseCommonSkeleton
          v-if="button"
          :class="$style.skeletonButton"
        />
      </div>

      <div
        :class="[
          $style.items,
          { [$style.isFirstChildBigger]: isFirstChildBigger },
        ]"
      >
        <BaseCommonSkeleton
          v-for="i in isSlideMode ? itemsPerRow : 24"
          :key="i"
          :class="$style.skeletonItem"
        />
      </div>
    </template>

    <template v-else>
      <PerfilOficialSwiper
        v-if="isSlideMode"
        :card-height="computedCardHeight"
        :gap="spaceBetweenInPixels"
        :icon-path="iconPath"
        :padding-bottom="showNumbers ? '16px' : undefined"
        :see-more-url="seeMoreUrl"
        :see-more-variant="'bordered'"
        :title="title"
      >
        <template #default="{ handleClick }">
          <div
            v-for="(game, index) in displayItems"
            :key="game.slug || index"
            :class="[
              $style.mediaItemWrapper,
              $style.swiperItem,
              { [$style.disabledMediaItem]: !game.slug },
              { [$style.top10SwiperItem]: showNumbers },
            ]"
          >
            <CasinoMediaApostatudo
              :ref="(el) => setCardRef(el, index)"
              :class="[
                $style.mediaCard,
                { [$style.isGame]: isGameMediaType },
                { [$style.shinyCard]: game.slug === 'pgsoft/fortune-horse' },
              ]"
              :game="game as Game"
              :index="index"
              :loading="getLoadingStrategy(index, itemsAboveFold)"
              :top10="showNumbers && isGameMediaType"
              :type="type"
              @click="handleClick"
            />
            <div
              v-if="showNumbers && index < MAX_NUMERIC_IMAGES"
              :class="[
                $style.mediaItemNumber,
                { [$style.mediaItemNumberTen]: index === 9 },
                { [$style.mediaItemNumberFour]: index === 3 },
              ]"
            >
              <NuxtImg
                :alt="`Number ${index + 1}`"
                :loading="getNumberLoadingStrategy(index)"
                :src="
                  getAssetsUrlForCDN(
                    `/assets/images/perfil-oficial/numbers/${String(index + 1).padStart(3, '0')}-number.webp`,
                  )
                "
              />
            </div>
            <div
              v-if="showNumbers && isGameMediaType"
              :class="$style.top10JogarButton"
              @click="onTop10PlayClick(index)"
            >
              <svg
                aria-hidden="true"
                :class="$style.top10PlayIcon"
                fill="currentColor"
                viewBox="0 0 8 10"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0 0L8 5L0 10Z" />
              </svg>
              <span :class="$style.top10JogarText">JOGAR</span>
            </div>
            <div
              v-if="!game.slug || game.slug === ''"
              :class="$style.comingSoonOverlay"
            >
              <span :class="$style.comingSoonText">EM BREVE</span>
            </div>
          </div>
        </template>
      </PerfilOficialSwiper>

      <div
        v-else
        :class="[
          $style.items,
          { [$style.isFirstChildBigger]: isFirstChildBigger },
        ]"
      >
        <div
          v-for="(game, index) in displayItems"
          :key="game.slug || index"
          :class="[
            $style.mediaItemWrapper,
            { [$style.disabledMediaItem]: !game.slug },
          ]"
        >
          <CasinoMediaApostatudo
            :class="[
              $style.mediaCard,
              { [$style.isGame]: isGameMediaType },
              { [$style.shinyCard]: game.slug === 'pgsoft/fortune-horse' },
            ]"
            :game="game as Game"
            :top10="showNumbers && isGameMediaType"
            :type="type"
          />
          <div
            v-if="showNumbers"
            :class="[
              $style.mediaItemNumber,
              { [$style.mediaItemNumberTen]: index === 9 },
              { [$style.mediaItemNumberFour]: index === 3 },
            ]"
          >
            <NuxtImg
              :alt="`Number ${index + 1}`"
              :loading="getNumberLoadingStrategy(index)"
              :src="
                getAssetsUrlForCDN(
                  `/assets/images/perfil-oficial/numbers/${String(index + 1).padStart(3, '0')}-number.webp`,
                )
              "
            />
          </div>
          <div
            v-if="!game.slug"
            :class="$style.comingSoonOverlay"
          >
            <span :class="$style.comingSoonText">EM BREVE</span>
          </div>
        </div>
        <template v-if="isLoadingMoreGames">
          <BaseCommonSkeleton
            v-for="index in 24"
            :key="`skeleton-${index}`"
            :class="$style.skeletonItem"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<style module lang="postcss">
.mediaItemNumber {
  @apply absolute select-none pointer-events-none;
  top: 0;
  left: 0;
  z-index: 0;
  height: 32%;

  img {
    @apply h-full w-auto object-contain;
  }
}

.mediaItemNumberTen {
  height: 31%;
  left: -5px;
}

.mediaItemNumberFour {
  left: -3px;
}

.mediaCard {
  @apply relative;
  z-index: 1; /* Increased z-index to ensure it's above the number */
}

.titleContainer {
  @apply flex items-center justify-center;
}
.listGames {
  @apply flex flex-col select-none w-full;
  /* No min-height to avoid empty spaces - aspect-ratio on items handles CLS prevention */
}

.title {
  @apply flex justify-start items-center pb-1.5 md:pb-2;
}

.items {
  @apply grid w-full;

  gap: v-bind(spaceBetweenInPixels);
  grid-template-columns: repeat(v-bind(itemsPerRow), minmax(0, 1fr));

  &.isFirstChildBigger {
    @media (min-width: 1024px) {
      a.mediaItem.isGame {
        &:nth-child(1) {
          grid-area: 1 / 1 / 3 / 3;
        }
      }
    }
  }
}

.skeletonTitle {
  @apply h-[20px] max-w-[200px] flex-1;
}

.skeletonButton {
  @apply h-[20px] max-w-[60px] flex-1;
}

.skeletonItem {
  @apply w-full h-auto;
  aspect-ratio: 25/29;
  min-height: 150px;
}

.navigationButton {
  @apply cursor-pointer text-texts/60;

  & + .navigationButton {
    @apply ml-3;
  }

  &.isNavigationButtonDisabled {
    @apply text-texts/20 cursor-not-allowed;
  }
}

.swiperItem {
  min-width: v-bind(mediaItemWidth);
  flex-shrink: 0;
  aspect-ratio: 25/29; /* Fixed aspect ratio to prevent CLS */
  height: auto;
}

.top10SwiperItem {
  flex-shrink: 0;
  /* Swiper aplica min-width inline nos slides; !important necessário para override */
  min-width: 0 !important;
  width: v-bind(mediaItemWidth);
  max-width: 149px;
  aspect-ratio: 150 / 199.5;
}

.mediaItemWrapper {
  @apply relative;
  z-index: 1;
}

.top10JogarButton {
  @apply absolute cursor-pointer select-none flex items-center justify-center;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  z-index: 10;
  width: 70%;
  aspect-ratio: 15 / 4;
  height: auto;
  background: var(--accent-color, #1414dd);
  border-radius: 100px;
  gap: 0.3em;
  color: #fcfcfc;
  container-type: inline-size;
}

.top10PlayIcon {
  height: 50%;
  width: auto;
  flex-shrink: 0;
}

.top10JogarText {
  font-weight: 500;
  font-size: clamp(7px, 12cqw, 12px);
  line-height: 1;
  white-space: nowrap;
  letter-spacing: 0.05em;
}

.disabledMediaItem {
  pointer-events: none;
  cursor: default;
}

.comingSoonOverlay {
  @apply absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 text-white text-lg font-bold rounded-md overflow-hidden;
  animation: pulse 2s ease-in-out infinite alternate;
  will-change: opacity;
  z-index: 90;
}

.comingSoonText {
  @apply text-[15px] md:text-[18px];
  background: linear-gradient(90deg, #ffffff, #ffffff70, #ffffff);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradient-animation 4s linear infinite;
  will-change: background-position;
}

@keyframes gradient-animation {
  to {
    background-position: 200% center;
  }
}
@keyframes pulse {
  0% {
    opacity: 0.84;
  }
  100% {
    opacity: 0.9;
  }
}

/* Shimmer shine effect */
.shinyCard {
  display: block;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
}

.shinyCard::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -100%;
  width: 50%;
  height: 200%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  transform: skewX(-25deg);
  animation: shimmerSlide 1.5s ease-out infinite;
  pointer-events: none;
  z-index: 35;
}

.shinyCard::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-shadow:
    inset 0 0 20px rgba(255, 255, 255, 0.3),
    0 0 15px rgba(0, 150, 255, 0.5);
  border-radius: 4px;
  animation: glowPulse 2s ease-in-out infinite;
  pointer-events: none;
  z-index: 35;
}

@keyframes shimmerSlide {
  0% {
    left: -100%;
  }
  100% {
    left: 150%;
  }
}

@keyframes glowPulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
